import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Cart() {
  const { items, setQty, remove, subtotal } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <ShoppingBag className="mx-auto text-muted-foreground" size={48} />
        <h1 className="font-heading font-bold text-2xl mt-4">Your cart is empty</h1>
        <Button className="mt-6" asChild data-testid="cart-shop-btn"><Link to="/shop/rice">Start shopping</Link></Button>
      </div>
    );

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight">Your Cart</h1>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items.map(({ product, quantity }) => (
            <div key={product.product_id} className="flex gap-4 bg-card border border-border rounded-lg p-3" data-testid={`cart-item-${product.product_id}`}>
              <img src={product.image} alt={product.name} className="w-20 h-20 rounded-md object-cover bg-muted" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground">{product.brand}</div>
                <div className="font-heading font-semibold leading-tight">{product.name}</div>
                <div className="text-xs text-muted-foreground">{product.pack_size}</div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center border border-border rounded-md">
                    <button className="p-1.5 hover:bg-muted" onClick={() => setQty(product.product_id, quantity - 1)} data-testid={`cart-dec-${product.product_id}`}><Minus size={14} /></button>
                    <span className="px-3 text-sm font-mono-points" data-testid={`cart-qty-${product.product_id}`}>{quantity}</span>
                    <button className="p-1.5 hover:bg-muted" onClick={() => setQty(product.product_id, quantity + 1)} data-testid={`cart-inc-${product.product_id}`}><Plus size={14} /></button>
                  </div>
                  <button className="text-destructive text-sm flex items-center gap-1 hover:underline" onClick={() => remove(product.product_id)} data-testid={`cart-remove-${product.product_id}`}><Trash2 size={14} /> Remove</button>
                </div>
              </div>
              <div className="font-heading font-bold whitespace-nowrap">{money(product.price * quantity)}</div>
            </div>
          ))}
        </div>
        <div className="bg-card border border-border rounded-lg p-5 h-fit">
          <h2 className="font-heading font-bold text-lg">Order Summary</h2>
          <div className="mt-4 flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono-points font-semibold">{money(subtotal)}</span></div>
          <div className="mt-1 text-xs text-muted-foreground">RicePoints applied at checkout (up to 15%).</div>
          <Button className="w-full mt-5" size="lg" data-testid="cart-checkout-btn" disabled={loading}
                  onClick={() => navigate(user ? "/checkout" : "/login")}>
            {loading ? "Loading…" : user ? "Proceed to Checkout" : "Login to Checkout"}
          </Button>
        </div>
      </div>
    </div>
  );
}
